import { memo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Virtuoso, VirtuosoHandle } from "react-virtuoso"
import { TextWithTooltip } from "@/components/text-with-tooltip"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { AgendaAction, AgendaEntryStatus } from "@/modules/patients/agenda"
import {
  AccessCell,
  ActionCell,
  AssignedCell,
  ChannelCell,
  DateCell,
  LanguageCell,
  ReasonCell,
  ServiceSpecialtyCell,
  StatusCell,
} from "@/modules/patients/agenda/agenda-cells"
import { AppointmentAgenda } from "@/modules/patients/libs/categorize-date-ranges"

interface AgendaColumn {
  id?: string
  header: string
  cell: (original: AppointmentAgenda) => React.ReactNode
}

const gridLayout =
  "minmax(180px, 1fr) minmax(40px, 1fr) minmax(0px, 0.5fr) minmax(0px, 0.6fr) minmax(0px, 0.8fr) minmax(0px, 1.5fr) minmax(0px, 1.5fr) minmax(0px, 0.85fr) 22px"
const HEADER_HEIGHT = 36
const ROW_HEIGHT = 40
const MAX_VISIBLE_ROWS = 15
const TABLE_MAX_HEIGHT = MAX_VISIBLE_ROWS * ROW_HEIGHT

interface AgendaTableProps {
  isEditMode: boolean
  data: AppointmentAgenda[]
  handleRowClick: (ch_TimeWindow: AppointmentAgenda, mode: AgendaAction) => void
  isPast?: boolean
  title: string
  agendaEntryStatus: AgendaEntryStatus
  selectedAgendaRowId: string | null
}

const AgendaTableContent = (props: AgendaTableProps) => {
  const { data, handleRowClick, isPast, isEditMode, title, agendaEntryStatus, selectedAgendaRowId } = props
  const { t } = useTranslation()
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const tableRef = useRef<HTMLDivElement>(null)

  const columns: AgendaColumn[] = [
    {
      header: t("patient.agenda.grid.columnName.date"),
      cell: (original) => <DateCell original={original} agendaEntryStatus={agendaEntryStatus} />,
    },
    {
      header: t("patient.agenda.grid.columnName.assignedTo"),
      cell: (original) => <AssignedCell original={original} />,
    },
    {
      header: t("patient.agenda.grid.columnName.language"),
      cell: (original) => <LanguageCell original={original} />,
    },
    {
      header: t("patient.agenda.grid.columnName.channel"),
      cell: (original) => <ChannelCell original={original} />,
    },
    {
      header: t("patient.agenda.grid.columnName.access"),
      cell: (original) => <AccessCell original={original} />,
    },
    {
      header: t("patient.agenda.grid.columnName.serviceAndSpecialty"),
      cell: (original) => <ServiceSpecialtyCell original={original} />,
    },
    {
      header: t("patient.agenda.grid.columnName.reasonForEncounter"),
      cell: (original) => <ReasonCell original={original} />,
    },
    {
      id: "status",
      header: t("patient.agenda.grid.columnName.status"),
      cell: (original) => <StatusCell original={original} />,
    },
    {
      id: "action",
      header: "",
      cell: (original) => (
        <ActionCell
          original={original}
          isEditMode={isEditMode}
          handleRowClick={handleRowClick}
          agendaEntryStatus={agendaEntryStatus}
        />
      ),
    },
  ]

  const onRowClick = (row: AppointmentAgenda) => {
    if (!tableRef.current) return
    handleRowClick(row, "EDIT")
  }

  const contentHeight = data.length * ROW_HEIGHT + HEADER_HEIGHT
  const tableHeight = Math.min(contentHeight, TABLE_MAX_HEIGHT)

  if (data.length === 0) {
    return null
  }

  return (
    <div className="mb-8 flex flex-col overflow-x-clip focus-within:z-10" data-testid={`${title}-appointments`}>
      <div className="bg-background-subdued flex h-10 shrink-0 items-center gap-2 py-2">
        <div className="text-on-surface text-base font-medium">{title}</div>
        <Badge
          className="bg-surface-subdued text-on-surface-subdued hover:bg-surface-subdued h-5 min-w-5 p-1 leading-4 font-normal"
          data-testid="ongoing-agenda-count">
          {data.length}
        </Badge>
      </div>
      <div className="focus-within:z-10" data-testid="agenda-table">
        <div ref={tableRef} className="agenda-table text-on-surface relative z-0 w-full">
          <div
            data-testid="table-header-container"
            className="border-border text-on-surface-subdued bg-surface sticky top-0 z-10 grid gap-4 rounded-t-xl border px-4 py-2 text-xs"
            style={{ gridTemplateColumns: gridLayout }}>
            {columns.map((column, index) => (
              <TextWithTooltip key={column.id ?? index} data-testid="table-header" className="flex items-center">
                <span className="truncate leading-4 tracking-[0.25px]">{column.header}</span>
              </TextWithTooltip>
            ))}
          </div>
          <Virtuoso
            ref={virtuosoRef}
            className="virtualized-table overflow-x-hidden"
            style={{ height: tableHeight - HEADER_HEIGHT }}
            data={data}
            fixedItemHeight={ROW_HEIGHT}
            components={{
              List: ({ children, ...props }) => (
                <div
                  {...props}
                  data-testid="table-row-container"
                  className="border-border bg-surface divide-y overflow-hidden rounded-b-xl border-x border-b text-sm">
                  {children}
                </div>
              ),
            }}
            itemContent={(_, row) => (
              <AgendaRow
                row={row}
                columns={columns}
                onRowClick={onRowClick}
                isPast={isPast}
                selectedAgendaRowId={selectedAgendaRowId}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}

export const AgendaTable = memo(AgendaTableContent)

interface AgendaRowProps {
  isPast?: boolean
  row: AppointmentAgenda
  columns: AgendaColumn[]
  onRowClick: (row: AppointmentAgenda) => void
  selectedAgendaRowId: string | null
}

const AgendaRowContent = ({ row, columns, isPast, onRowClick, selectedAgendaRowId }: AgendaRowProps) => {
  return (
    <div
      data-testid={row.appointment?.id ?? row.id}
      className={cn(
        "border-border text-on-surface hover:bg-surface-hovered active:bg-surface-pressed relative grid h-10 cursor-pointer items-center justify-center gap-4 px-4 py-2 text-sm",
        { "bg-surface-disabled text-on-surface-disabled": isPast },
        {
          "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed":
            selectedAgendaRowId === row.id,
        },
      )}
      style={{ gridTemplateColumns: gridLayout }}
      onClick={() => onRowClick(row)}>
      {columns.map((column, index) => (
        <span key={column.id ?? index} className="truncate" data-testid="table-cell">
          {column.cell(row)}
        </span>
      ))}
    </div>
  )
}

const AgendaRow = memo(AgendaRowContent)
